import {
  HttpClient,
  HttpContext,
  HttpContextToken
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import {
  Company,
  CompanyFee,
  CompanyServiceRanking,
  ExemptVendorType,
  Note,
  VendorRanking
} from '../classes/models';
import { CompanyComplianceType } from '../classes/models/company-compliance-type';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  saveCompany(
    Company: Company,
    query = {},
    optional: { manuallyHandleError?: boolean } = {}
  ) {
    const { manuallyHandleError } = optional;
    const context = new HttpContext();
    if (manuallyHandleError) {
      const ContextToken = new HttpContextToken(() => 'ManuallyHandleError');
      context.set(ContextToken, null);
    }

    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Company>;
        } = {
          Admin: () =>
            this.http.post(`/api/v2/admin/company/save`, Company, {
              params: query,
              context
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompany(CompanyId: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Company>;
        } = {
          Vendor: () =>
            this.http.get<Company>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${CompanyId}/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get<Company>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/one`,
              { params: query }
            ),

          // Will need to implement this. Admin can use either Client or Vendor route or a custom route for Admins.
          Admin: () =>
            this.http.get(`/api/v2/admin/company/${CompanyId}/one`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanies(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Company[]>;
        } = {
          Vendor: () =>
            this.http.get<Company[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<Company[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/many`,
              { params: query }
            ),

          // Will need to implement this. Admin can use either Client or Vendor route or a custom route for Admins.
          Admin: () =>
            this.http.get<Company[]>(`/api/v2/admin/company/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/count`,
              { params: query }
            ),

          // Will need to implement this. Admin can use either Client or Vendor route or a custom route for Admins.
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/company/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyInternalNotes(CompanyId: number) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Note[]>;
        } = {
          Admin: () =>
            this.http.get<Note[]>(
              `/api/v2/admin/company/${CompanyId}/internalNote/many`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveCompanyInternalNote(CompanyId: number, note: string) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Note>;
        } = {
          Admin: () =>
            this.http.post<Note>(
              `/api/v2/admin/company/${CompanyId}/internalNote`,
              { note }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteCompanyInternalNote(CompanyId: number, NoteId: number) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<void>;
        } = {
          Admin: () =>
            this.http.delete<void>(
              `/api/v2/admin/company/${CompanyId}/internalNote/${NoteId}`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyFees(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<CompanyFee[]>;
        } = {
          Vendor: () =>
            this.http.get<CompanyFee[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/companyFee/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<CompanyFee[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyFee/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<CompanyFee[]>(
              `/api/v2/admin/company/companyFee/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyFeeCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/companyFee/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyFee/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/company/companyFee/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteCompanyFee(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.delete(`/api/v2/admin/company/companyFee`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  downloadCompanyUserUploadTemplate(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get(
              `/api/v2/admin/company/downloadCompanyUserUploadTemplate`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  processClientRegisteredVendorsFileUpload(
    fileData: any,
    query = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(
              `/api/v2/admin/company/clientRegisteredVendor/processClientRegisteredVendorFileUpload`,
              fileData,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  processClientUsersFileUpload(
    CompanyId: number,
    fileData: any,
    query = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(
              `/api/v2/admin/company/${CompanyId}/user/processClientUsersFileUpload`,
              fileData,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  downloadClientRegisteredVendorUploadTemplate(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get(
              `/api/v2/admin/company/clientRegisteredVendor/downloadClientRegisteredVendorUploadTemplate`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  downloadExemptVendorVendorUploadTemplate(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get(
              `/api/v2/admin/company/exemptVendor/downloadUploadTemplate`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  processExemptVendorsFileUpload(fileData: any, query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(
              `/api/v2/admin/company/exemptVendor/processFileUpload`,
              fileData,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyComplianceTypes(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<CompanyComplianceType[]>;
        } = {
          Vendor: () =>
            this.http.get<CompanyComplianceType[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/companyComplianceType/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<CompanyComplianceType[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyComplianceType/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<CompanyComplianceType[]>(
              `/api/v2/admin/company/companyComplianceType/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveCompanyComplianceType(
    CompanyComplianceType: CompanyComplianceType,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<CompanyComplianceType>;
        } = {
          Admin: () =>
            this.http.post<CompanyComplianceType>(
              `/api/v2/admin/company/companyComplianceType`,
              CompanyComplianceType,
              { params: query }
            )
          // 'Client': () => this.http.post<CompanyComplianceType>(`/api/v2/company/${this.AuthService.getUserCompanyId()}/companyComplianceType`, CompanyComplianceType, { params: query }),
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteCompanyComplianceType(CompanyComplianceTypeId: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.delete(
              `/api/v2/admin/company/companyComplianceType/${CompanyComplianceTypeId}`,
              { params: query }
            ),
          Client: () =>
            this.http.delete(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyComplianceType/${CompanyComplianceTypeId}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  // public getCompany(CompanyId: number, query = {}): Observable<any> {
  //   return this.http.get(`/api/v1/company/${CompanyId}`, { params: query });
  // }
  public getCompanyExemptVendorTypes(query: {} = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ExemptVendorType[]>;
        } = {
          Client: () =>
            this.http.get<ExemptVendorType[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendorType/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<ExemptVendorType[]>(
              `/api/v2/admin/exemptVendorType/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyExemptVendorType(query: {} = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ExemptVendorType>;
        } = {
          Client: () =>
            this.http.get<ExemptVendorType>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendorType/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyExemptVendorTypeCount(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendorType/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/exemptVendorType/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveCompanyExemptVendorType(
    ExemptVendorType: ExemptVendorType,
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ExemptVendorType>;
        } = {
          Client: () =>
            this.http.post<ExemptVendorType>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendorType`,
              ExemptVendorType,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public deleteCompanyExemptVendorType(
    ExemptVendorTypeId: number,
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete<any>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendorType/${ExemptVendorTypeId}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getContractCount(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<any>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyServiceRankings(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any[]>;
        } = {
          Client: () =>
            this.http.get<any[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyServiceRanking/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyServiceRanking(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any[]>;
        } = {
          Client: () =>
            this.http.get<any[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyServiceRanking/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyServiceRankingCount(query: {} = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyServiceRanking/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveCompanyServiceRanking(
    CompanyServiceRanking: CompanyServiceRanking,
    query: {} = {}
  ): Observable<CompanyServiceRanking> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<CompanyServiceRanking>;
        } = {
          Client: () =>
            this.http.post<CompanyServiceRanking>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyServiceRanking`,
              CompanyServiceRanking,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public deleteCompanyServiceRanking(
    CompanyServiceRankingId: number,
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete<any>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyServiceRanking/${CompanyServiceRankingId}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
  public getCompanyVendorStatuses(CompanyId: number): Observable<any> {
    return this.http.get(
      `/api/v1/companyVendor/company/${CompanyId}/getCompanyVendorStatuses`
    );
  }
  public getCompanyVendorTrades(CompanyId: number): Observable<any> {
    return this.http.get(
      `/api/v1/companyVendor/company/${CompanyId}/getCompanyVendorTrades`
    );
  }
  public getProposals(CompanyId: number, query = {}): Observable<any> {
    return this.http.get(`/api/v2/company/${CompanyId}/qspr`, {
      params: query
    });
  }
  // Replaced for now with new service calls below
  // public getApConfig(CompanyId: number, query: {} = {}): Observable<any> {
  //   return this.http.get(`/api/v2/company/${CompanyId}/ap/config`, { params: query })
  // }

  // The service calls below still need to be tested.
  public saveVendorRank(
    VendorId: number,
    VendorRank: VendorRanking,
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.put(
              `/api/v1/company/${this.AuthService.getUserCompanyId()}/vendor/${VendorId}/setVendorRank`,
              VendorRank,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public deletePublicApiAccessKey(
    companyId: number,
    apiAccessKeyId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `/api/v3/companies/${companyId}/public-api/api-access-key/${apiAccessKeyId}`
    );
  }

  public getCompanyLogoS3PresignedUrl(
    CompanyId: number
  ): Observable<S3PresignedUrl> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Vendor: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${CompanyId}/getCompanyLogoS3PresignedUrl`
            ),
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${CompanyId}/getCompanyLogoS3PresignedUrl`
            ),
          Admin: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/admin/company/${CompanyId}/getCompanyLogoS3PresignedUrl`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
